/**
 * Workspace index of all measurement scales across scoring attributes.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ruler, Search } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import type { MeasurementScale, MeasurementScaleType } from '../../../types/prioritization/lifecycle';

const SCALE_TYPE_LABELS: Record<MeasurementScaleType, string> = {
  numeric: 'Numeric range',
  ordinal: 'Ordinal labels',
  binary: 'Binary',
  percentage: 'Percentage',
  currency: 'Currency',
  fibonacci: 'Fibonacci',
  custom: 'Custom',
};

type ScaleRow = {
  attributeKey: string;
  attributeLabel: string;
  scale: MeasurementScale;
  isDefault: boolean;
};

export default function ValueScalesSettings() {
  const attributes = useAttributesStore((s) => s.attributes);
  const [search, setSearch] = useState('');

  const scoringAttributes = useMemo(
    () => attributes.filter((a) => !a.isCustomField),
    [attributes],
  );

  const rows: ScaleRow[] = useMemo(() => {
    const out: ScaleRow[] = [];
    for (const attr of scoringAttributes) {
      const scales = (attr.scales ?? []) as MeasurementScale[];
      const defId = attr.defaultScaleId;
      for (const scale of scales) {
        out.push({
          attributeKey: attr.key,
          attributeLabel: attr.label,
          scale,
          isDefault: scale.id === defId,
        });
      }
    }
    return out;
  }, [scoringAttributes]);

  const q = search.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.attributeLabel.toLowerCase().includes(q) ||
        r.attributeKey.toLowerCase().includes(q) ||
        (r.scale.label || '').toLowerCase().includes(q) ||
        SCALE_TYPE_LABELS[r.scale.scaleType].toLowerCase().includes(q),
    );
  }, [rows, q]);

  const attributesWithScales = useMemo(
    () => scoringAttributes.filter((a) => (a.scales?.length ?? 0) > 0).length,
    [scoringAttributes],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Ruler className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Value scales"
        subtitle="All measurement scales defined on scoring attributes. Open an attribute to add presets, edit points, or set the default scale."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Scoring attributes</p>
          <p className="text-2xl font-bold text-[var(--text-pri)]">{scoringAttributes.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Attributes with scales</p>
          <p className="text-2xl font-bold text-primary-600">{attributesWithScales}</p>
        </div>
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total scales</p>
          <p className="text-2xl font-bold text-[var(--text-pri)]">{rows.length}</p>
        </div>
      </div>

      <div className="card">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
          <input
            type="search"
            placeholder="Search by attribute, scale, or type…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm"
          />
        </div>

        {filteredRows.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-ter)] text-sm">
            {rows.length === 0
              ? 'No measurement scales yet. Add scales from an attribute’s scale editor.'
              : 'No scales match your search.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead>
                <tr className="bg-[var(--surface)]">
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Attribute</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Scale</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Values</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Default</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] uppercase" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)] bg-[var(--bg-raised)]">
                {filteredRows.map((r) => {
                  const summary =
                    r.scale.points.length === 0
                      ? '—'
                      : r.scale.points.length <= 4
                        ? r.scale.points.map((p) => p.label).join(', ')
                        : `${r.scale.points.length} values`;
                  return (
                    <tr key={`${r.attributeKey}-${r.scale.id}`} className="hover:bg-[var(--surface)]">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-[var(--text-pri)]">{r.attributeLabel}</div>
                        <div className="text-xs font-mono text-[var(--text-ter)]">{r.attributeKey}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--text-pri)]">{r.scale.label || 'Untitled scale'}</td>
                      <td className="px-4 py-3 text-sm text-[var(--text-sec)]">{SCALE_TYPE_LABELS[r.scale.scaleType]}</td>
                      <td className="px-4 py-3 text-sm text-[var(--text-sec)] max-w-xs truncate" title={summary}>
                        {summary}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {r.isDefault ? (
                          <span className="text-primary-700 font-medium text-xs">Default</span>
                        ) : (
                          <span className="text-[var(--text-ter)] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <Link
                          to={`/prioritization/settings/value-scales/${encodeURIComponent(r.attributeKey)}`}
                          className="text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline"
                        >
                          Manage
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
